import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { QuizRenderer } from '@/features/ai-agents/chat/QuizRenderer';
import { dispatchChatLightboxEvent } from '@/features/ai-agents/chat/chat-lightbox-event';
import { breakpoints } from '@/components/responsive';

interface MarkdownRendererProps {
  children?: string;
  classname?: string;

  inline?: boolean;
}

const tableComponents: Components = {
  table({ children }) {
    return (
      <TableWrapper>
        <Table>{children}</Table>
      </TableWrapper>
    );
  },
  thead({ children }) {
    return <Thead>{children}</Thead>;
  },
  tbody({ children }) {
    return <Tbody>{children}</Tbody>;
  },
  tr({ children }) {
    return <Tr>{children}</Tr>;
  },
  th({ children }) {
    return <Th>{children}</Th>;
  },
  td({ children }) {
    return <Td>{children}</Td>;
  },
};

const components: Components = {
  ...tableComponents,
  hr() {
    return <Seperator />;
  },
  code(props) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { children, className, node, ...rest } = props;

    if (className?.includes(`vsly/json/quiz`)) {
      return <QuizRenderer>{children}</QuizRenderer>;
    }

    if (className?.includes(`vsly/json/close`)) {
      return (
        <p style={{ fontFamily: `Eesti`, fontWeight: `bold` }}>
          You can close the chat while I’m building the experience, this might
          take a while, and the status will update automatically once it’s
          ready.
        </p>
      );
    }

    if (className?.includes(`vsly/json`)) {
      return null;
    }

    return (
      <code className={className} {...rest}>
        {children}
      </code>
    );
  },
  img(props) {
    return (
      <Image
        src={props.src}
        alt={props.alt}
        onClick={() => props.src && dispatchChatLightboxEvent(props.src)}
      />
    );
  },
  a(props) {
    const { href } = props;

    const handler = () => {
      if (href) {
        window.postMessage(href);
      }
    };

    const executePlan = () => {
      window.dispatchEvent(
        new CustomEvent(`ai-chat-input`, {
          detail: `Go ahead with the plan`,
        }),
      );
    };

    if (
      [`#edit-visual`, `#edit-code`].includes(href) ||
      href.includes(`#edit-strategy`)
    ) {
      return (
        <>
          <br />
          <EditButton onClick={handler}>{props.children}</EditButton>
        </>
      );
    }

    if (href?.includes(`execute-plan`)) {
      return (
        <ExecutePlanButton onClick={executePlan}>
          {props.children}
        </ExecutePlanButton>
      );
    }

    return (
      <a href={props.href} target="_blank" rel="noreferrer">
        {props.children}
      </a>
    );
  },
};

export function MarkdownRenderer({
  children,
  classname,
  inline = false,
}: MarkdownRendererProps) {
  return (
    <Wrapper>
      <ReactMarkdown
        className={classname}
        components={inline ? {} : components}
        remarkPlugins={[remarkGfm]}
        linkTarget="_blank"
      >
        {children}
      </ReactMarkdown>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  br:not(:first-child) {
    display: none;
  }
`;

const EditButton = styled.button`
  appearance: none;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: black;
  border: 1px solid black;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  transition: background-color 0.3s ease-in-out, color 0.3s ease-in-out;
  cursor: pointer;
  margin-top: 1rem;
  margin-right: 0.5rem;

  &:hover {
    background: black;
    color: white;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  margin: 1rem 0;
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  border-radius: 0.5rem;
  font-family: Eesti, sans-serif;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.4rem;
`;

const Thead = styled.thead`
  background-color: rgba(0, 0, 0, 0.04);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const Tbody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
`;

const Tr = styled.tr``;

const Th = styled.th`
  padding: 1.2rem 1.6rem;
  text-align: left;
  font-weight: 600;
  color: #111;
  white-space: nowrap;
  font-size: 1.4rem;
`;

const Td = styled.td`
  padding: 1.2rem 1.6rem;
  vertical-align: top;
  color: #333;
  line-height: 1.55;
`;

const Seperator = styled.div`
  width: 80%;
  margin: 4rem auto;
  height: 1px;
  background-color: rgba(0, 0, 0, 0.05);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.02);
`;

const ExecutePlanButton = styled.button`
  width: 25rem;
  padding: 1.2rem 1.5rem;
  background: #111111;
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.01em;
  transition: background 0.2s;
  margin-top: 2rem;

  &:hover:not(:disabled) {
    background: #333;
  }

  &:disabled {
    background: #aaa;
    cursor: not-allowed;
  }

  ${breakpoints.down(`md`)} {
    max-width: 100%;
  }
`;

const Image = styled.img`
  width: 25rem;
  max-height: 15rem;
  object-fit: contain;
  object-position: top;
  cursor: zoom-in;
  border-radius: 0.4rem;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.85;
  }
`;
